import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import { Check, Gift } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REWARD_LABEL = {
  half_off_6th: "6th Treatment — 50% OFF",
  free_10th:    "10th Treatment — FREE",
};

/** CLAÈ Loyalty Card — public tokenised view. Same access pattern as the
 * aftercare portal: no login, no account, the emailed URL is the key. */
export default function Loyalty() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/loyalty/${token}`)
      .then((r) => mounted && setData(r.data))
      .catch((e) => mounted && setErr(e?.response?.data?.detail || "Loyalty card not found"));
    return () => { mounted = false; };
  }, [token]);

  if (err) {
    return (
      <div className="clae-home">
        <Header />
        <section className="clae-page">
          <p className="clae-eyebrow">CLAÈ LOYALTY</p>
          <h1 className="clae-h1 clae-h1--sm">CARD NOT FOUND</h1>
          <p className="clae-body" data-testid="loyalty-error">{err}</p>
        </section>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="clae-home">
        <Header />
        <section className="clae-page">
          <p className="clae-eyebrow">CLAÈ LOYALTY</p>
          <p className="clae-body">Loading your loyalty card…</p>
        </section>
      </div>
    );
  }

  const { progress, next_goal, half_off_unlocked, half_off_redeemed,
          free_10_unlocked, free_10_redeemed, unredeemed_rewards,
          past_cycles, client_first_name, cycle_index } = data;

  const inFirstMilestone = progress < 5 && !half_off_unlocked;
  const cycleTarget = inFirstMilestone ? 5 : 9;
  const remaining = Math.max(0, next_goal.target - progress);

  // Reward-unlocked banner text
  let banner = null;
  if (progress >= 9 && free_10_unlocked && !free_10_redeemed) {
    banner = { title: "CONGRATULATIONS", body: "You've unlocked your 10th treatment completely FREE.", pct: "FREE" };
  } else if (progress >= 5 && half_off_unlocked && !half_off_redeemed && progress < 9) {
    banner = { title: "CONGRATULATIONS", body: "You've unlocked 50% OFF your next treatment.", pct: "50% OFF" };
  }

  return (
    <div className="clae-home" data-testid="loyalty-page">
      <Header />
      <section className="clae-page" style={{ maxWidth: 620 }}>
        <p className="clae-eyebrow" style={{ textAlign: "center" }}>THANK YOU FOR VISITING</p>
        <h1 className="clae-h1 clae-h1--sm" style={{ textAlign: "center" }}>
          THE CLAÈ<br />LOYALTY CARD
        </h1>
        <div style={{ height: 2, width: 46, background: "#C5A059", margin: "12px auto 8px" }} />
        <p className="mono-label" style={{ textAlign: "center", color: "#66615C", margin: "0 0 32px" }}>
          MORE TREATMENTS, MORE REWARDS
        </p>

        {client_first_name && (
          <p className="clae-body" style={{ textAlign: "center", marginBottom: 28 }}>
            Hi {client_first_name}, here is your loyalty progress.
          </p>
        )}

        {/* ── Card ────────────────────────────────────────────── */}
        <div
          data-testid="loyalty-card"
          style={{
            background: "#FAF9F6",
            border: "1px solid rgba(197,160,89,0.28)",
            padding: "28px 24px",
            marginBottom: 24,
          }}
        >
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 20 }}>
            <div>
              <p className="mono-label" style={{ margin: 0, color: "#66615C" }}>THE CLAÈ</p>
              <p className="mono-label" style={{ margin: 0, color: "#141414", letterSpacing: "0.24em" }}>LOYALTY CARD</p>
            </div>
            <div
              data-testid="loyalty-progress-count"
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                background: "#F5F1E9",
                border: "1px solid rgba(197,160,89,0.28)",
                padding: "6px 14px",
                letterSpacing: "0.12em",
              }}
            >
              {progress} / {cycleTarget}
            </div>
          </div>

          {/* Stamp row */}
          <div
            data-testid="loyalty-stamps"
            style={{ display: "flex", gap: 10, justifyContent: "space-between", marginBottom: 20 }}
          >
            {Array.from({ length: cycleTarget }).map((_, i) => {
              const filled = i < progress;
              return (
                <div
                  key={i}
                  data-testid={`loyalty-stamp-${i + 1}`}
                  data-filled={filled ? "yes" : "no"}
                  style={{
                    flex: 1,
                    aspectRatio: "1 / 1",
                    borderRadius: 999,
                    background: filled ? "#B79867" : "transparent",
                    border: filled ? "1px solid #B79867" : "1px dashed rgba(197,160,89,0.55)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#FAF9F6",
                    maxWidth: 60,
                  }}
                >
                  {filled && <Check size={18} strokeWidth={2} />}
                </div>
              );
            })}
          </div>

          {banner ? (
            <div
              data-testid="loyalty-banner"
              style={{
                background: "#F5F1E9",
                border: "1px solid rgba(197,160,89,0.4)",
                padding: "16px 18px",
                display: "flex",
                gap: 16,
                alignItems: "center",
              }}
            >
              <div
                style={{
                  width: 64, height: 64, borderRadius: 999,
                  background: "#B79867", color: "#FAF9F6",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 16, letterSpacing: "0.06em",
                  flexShrink: 0, textAlign: "center", lineHeight: 1.1,
                }}
              >
                {banner.pct}
              </div>
              <div>
                <p className="mono-label" style={{ margin: 0, color: "#66615C" }}>{banner.title}</p>
                <p className="clae-body" style={{ margin: "4px 0 0", fontSize: 14 }}>{banner.body}</p>
              </div>
            </div>
          ) : (
            <p className="clae-body" style={{ margin: 0, textAlign: "center", fontSize: 14 }} data-testid="loyalty-progress-copy">
              {/* Belt-and-braces: never render "0 more treatments..." — the backend
                  rolls the display into a fresh cycle after the free reward is
                  redeemed, so remaining should always be > 0 here. */}
              {remaining <= 0
                ? "Keep stamping to unlock your next reward."
                : remaining === 1
                ? `1 more treatment until your ${inFirstMilestone ? "6th" : "10th"} treatment is ${inFirstMilestone ? "50% OFF" : "FREE"}.`
                : `${remaining} more treatments until your ${inFirstMilestone ? "6th" : "10th"} treatment is ${inFirstMilestone ? "50% OFF" : "FREE"}.`}
            </p>
          )}
        </div>

        {/* ── Rewards summary ─────────────────────────────────── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12, marginBottom: 10 }}>
          <div style={{ background: "#F5F1E9", border: "1px solid rgba(197,160,89,0.28)", padding: 18, textAlign: "center" }}>
            <p className="mono-label" style={{ margin: 0, color: "#66615C" }}>AFTER 5 TREATMENTS</p>
            <p className="clae-body" style={{ margin: "6px 0 0", fontSize: 15 }}>Your 6th treatment is 50% OFF</p>
          </div>
          <div style={{ background: "#F5F1E9", border: "1px solid rgba(197,160,89,0.28)", padding: 18, textAlign: "center" }}>
            <p className="mono-label" style={{ margin: 0, color: "#66615C" }}>AFTER 9 TREATMENTS</p>
            <p className="clae-body" style={{ margin: "6px 0 0", fontSize: 15 }}>Your 10th treatment is FREE</p>
          </div>
        </div>
        <p className="mono-label" data-testid="loyalty-course-rule" style={{ color: "#66615C", textAlign: "center", fontSize: 10, letterSpacing: "0.14em", marginBottom: 28 }}>
          Course treatments count as one stamp only.
        </p>

        {unredeemed_rewards?.length > 0 && (
          <div data-testid="loyalty-unredeemed" style={{ marginBottom: 28 }}>
            <p className="mono-label" style={{ color: "#66615C", marginBottom: 8 }}>UNREDEEMED REWARDS</p>
            {unredeemed_rewards.map((r) => (
              <div
                key={r.id}
                data-testid={`loyalty-unredeemed-${r.kind}`}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderTop: "1px solid #E5E1D8" }}
              >
                <Gift size={16} />
                <span className="clae-body" style={{ fontSize: 14 }}>{REWARD_LABEL[r.kind] || r.label}</span>
              </div>
            ))}
            <p className="mono-label" style={{ color: "#66615C", marginTop: 12, fontSize: 10 }}>
              Automatically applied at your next qualifying booking — no code required.
            </p>
          </div>
        )}

        {past_cycles?.length > 0 && (
          <div data-testid="loyalty-history">
            <p className="mono-label" style={{ color: "#66615C", marginBottom: 8 }}>LOYALTY HISTORY</p>
            {past_cycles.map((c) => (
              <div key={c.cycle_index} style={{ borderTop: "1px solid #E5E1D8", padding: "14px 0" }}>
                <p className="mono-label" style={{ color: "#141414", margin: 0 }}>CYCLE {c.cycle_index}</p>
                <p className="clae-body" style={{ fontSize: 13, margin: "4px 0" }}>
                  {c.stamps} qualifying treatments completed
                </p>
                {c.rewards.map((r, i) => (
                  <p key={`${r.kind}-${r.redeemed_at || "u"}-${i}`} className="clae-body" style={{ fontSize: 13, margin: "4px 0", color: "#66615C" }}>
                    → {REWARD_LABEL[r.kind] || r.label}
                    {r.redeemed_at ? " · Redeemed" : " · Unlocked"}
                    {r.redeemed_booking_reference ? ` · ${r.redeemed_booking_reference}` : ""}
                  </p>
                ))}
              </div>
            ))}
          </div>
        )}

        <p className="mono-label" style={{ color: "#B7B1A6", textAlign: "center", marginTop: 40, fontSize: 10 }}>
          Cycle {cycle_index} · Updated automatically after every completed treatment
        </p>
      </section>
    </div>
  );
}
